import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, CenteredLoader, InformationBox, useToast } from '@app/core';
import {
  useLoginWithEmailValidationCode,
  useLogout,
  useMe,
  useParticipation,
  usePublicSession,
} from '@app/api';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  message: {
    ...theme.fonts.caption,
  },
  h1: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 4,
  },
  container: {
    width: theme.maxContentWidth.largeForm,
    margin: '0 auto',
    padding: theme.marginBase * 2,
    marginTop: theme.marginBase * 4,
    maxWidth: '100%',
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
    gap: theme.marginBase,
  },
}));
export const StudyStart = () => {
  const classes = useStyles();
  const { id, accountId } = useParams();
  const { t } = useTranslation();
  const { data: participation } = useParticipation(id);
  const { data: session } = usePublicSession(participation?.sessionId);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me, isLoading } = useMe(true);
  const { mutateAsync: loginWithCode } = useLoginWithEmailValidationCode();
  const { mutateAsync: logout } = useLogout({ noRedirect: true });

  const error = new URLSearchParams(location.search).get('error');
  const code = new URLSearchParams(location.search).get('connectionCode');

  useEffect(() => {
    (async () => {
      if (isLoading || error) return;

      if (code && accountId && !error && !me) {
        try {
          const login = await loginWithCode({
            accountId,
            code,
          });
          if (!login) {
            toast.error(t('auth.login.action.studyStart.error'));
          }
        } catch (e) {
          toast.error(t('auth.login.action.studyStart.error'));
        }
      }
      navigate(`/p/${id}`);
    })();
  }, [
    accountId,
    code,
    error,
    id,
    isLoading,
    loginWithCode,
    me,
    navigate,
    t,
    toast,
  ]);

  if (error === 'not-good-account') {
    return (
      <div className={classes.container}>
        <h1 className={classes.h1}>
          <Trans i18nKey="auth.login.action.studyStart.notGoodAccount.title" />
        </h1>
        <InformationBox variant="danger">
          <div className={classes.message}>
            <Trans
              i18nKey="auth.login.action.studyStart.notGoodAccount.error"
              components={{
                b: <b />,
              }}
              values={{
                study: session?.title || '',
              }}
            ></Trans>
          </div>
        </InformationBox>
        <div className={classes.buttonsContainer}>
          <Button small to="/" color="ink" variant="muted">
            <Trans i18nKey="auth.login.action.studyFinish.mySpace"></Trans>
          </Button>
          <Button
            small
            onClick={async () => {
              await logout();
              navigate(`/p/${id}`);
            }}
          >
            <Trans i18nKey="auth.login.action.studyStart.logOut"></Trans>
          </Button>
        </div>
      </div>
    );
  }

  return <CenteredLoader />;
};
